// src/utils/ruleEngine.js

export const LOGIC = {
  AND: "AND",
  OR: "OR",
};

export const OPERATORS = [
  { label: "=", value: "EQ" },
  { label: "!=", value: "NEQ" },
  { label: "contains", value: "CONTAINS" },
  { label: "not contains", value: "NOT_CONTAINS" },
  { label: ">", value: "GT" },
  { label: ">=", value: "GTE" },
  { label: "<", value: "LT" },
  { label: "<=", value: "LTE" },
  { label: "in (comma)", value: "IN" },
];

const toNumberOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(String(v).replace("%", "").trim());
  return Number.isFinite(n) ? n : null;
};

const normalizeStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

const evalCondition = (row, cond) => {
  const { field, operator, value } = cond;

  // Missing field ⇒ FAIL (safe default)
  if (!row || !(field in row)) return false;

  const leftRaw = row[field];
  const leftStr = normalizeStr(leftRaw);
  const rightStr = normalizeStr(value);

  switch (operator) {
    case "EQ":
      return leftStr.toUpperCase() === rightStr.toUpperCase();
    case "NEQ":
      return leftStr.toUpperCase() !== rightStr.toUpperCase();
    case "CONTAINS":
      return leftStr.toUpperCase().includes(rightStr.toUpperCase());
    case "NOT_CONTAINS":
      return !leftStr.toUpperCase().includes(rightStr.toUpperCase());
    case "IN": {
      const set = rightStr
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .map((x) => x.toUpperCase());
      return set.includes(leftStr.toUpperCase());
    }
    case "GT":
    case "GTE":
    case "LT":
    case "LTE": {
      const l = toNumberOrNull(leftRaw);
      const r = toNumberOrNull(value);
      if (l === null || r === null) return false;
      if (operator === "GT") return l > r;
      if (operator === "GTE") return l >= r;
      if (operator === "LT") return l < r;
      return l <= r;
    }
    default:
      return false;
  }
};

const combine = (bools, logic) => {
  if (!bools.length) return true;
  return logic === LOGIC.OR ? bools.some(Boolean) : bools.every(Boolean);
};

/**
 * ruleSet:
 * {
 *   id?, name?,
 *   groupLogic: "AND"|"OR",
 *   groups: [
 *     {
 *       timeFrame: 60,
 *       conditionLogic: "AND"|"OR",
 *       conditions: [{ field, operator, value }]
 *     }
 *   ]
 * }
 */
export const getQualifiedSymbols = (signals, ruleSet) => {
  if (!ruleSet || !Array.isArray(ruleSet.groups) || ruleSet.groups.length === 0) {
    return []; // no rule ⇒ no override
  }

  // Build map: symbol -> tf -> row
  const bySymbol = new Map();
  for (const row of signals || []) {
    const sym = row?.symbol;
    const tf = Number(row?.timeFrame);
    if (!sym || !Number.isFinite(tf)) continue;

    if (!bySymbol.has(sym)) bySymbol.set(sym, new Map());
    bySymbol.get(sym).set(tf, row);
  }

  const qualified = [];
  for (const [sym, tfMap] of bySymbol.entries()) {
    const groupResults = ruleSet.groups.map((g) => {
      const tf = Number(g.timeFrame);
      const row = tfMap.get(tf);

      // Missing TF row ⇒ FAIL (safe default)
      if (!row) return false;

      const condResults = (g.conditions || []).map((c) => evalCondition(row, c));
      return combine(condResults, g.conditionLogic || LOGIC.AND);
    });

    const ok = combine(groupResults, ruleSet.groupLogic || LOGIC.AND);
    if (ok) qualified.push(sym);
  }

  return qualified;
};
